// ============================================================================
// BOOKING EMAILS — the "email the account team lead" toggle and the history of
// emails sent for one booking, shown in the shared BookingModal.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Send, RefreshCw, AlertCircle, CheckCircle2, MinusCircle, Loader } from 'lucide-react';
import {
  BookingEmailRecord,
  EMAIL_TYPE_LABELS,
  listBookingEmails,
  retryBookingEmail
} from '../../services/booking-emails';

interface Props {
  bookingId: string;
  hasAccountManager: boolean;
  enabled: boolean;
  canEdit: boolean;
  canRetry: boolean;
  saving: boolean;
  refreshKey: string;           // changes after a booking update so the history refetches
  onToggle: (enabled: boolean) => void;
}

const STATUS_ICON = {
  sent: <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />,
  sending: <Loader className="w-3.5 h-3.5 text-slate-400 animate-spin" />,
  skipped: <MinusCircle className="w-3.5 h-3.5 text-slate-400" />,
  failed: <AlertCircle className="w-3.5 h-3.5 text-red-500" />
};

function when(iso?: string): string {
  return iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

export default function BookingEmails({ bookingId, hasAccountManager, enabled, canEdit, canRetry, saving, refreshKey, onToggle }: Props) {
  const [emails, setEmails] = useState<BookingEmailRecord[]>([]);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listBookingEmails(bookingId)
      .then(r => { setEmails(r.emails); setMaxAttempts(r.maxAttempts); })
      .catch(() => undefined);   // history is informational; the toggle still works
  }, [bookingId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  async function retry(record: BookingEmailRecord) {
    setRetrying(record.recordKey);
    setError(null);
    try {
      await retryBookingEmail(bookingId, record.recordKey);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  }

  return (
    <div className="flex gap-2.5 text-sm items-start">
      <Send className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 flex flex-col gap-2">
        <label className={`flex items-center gap-2 ${canEdit && hasAccountManager ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
          <input
            type="checkbox"
            checked={hasAccountManager && enabled}
            disabled={!canEdit || !hasAccountManager || saving}
            onChange={e => onToggle(e.target.checked)}
            className="w-4 h-4 accent-primary rounded"
          />
          <span className="text-navy">Email the account team lead</span>
          {!hasAccountManager && <span className="text-xs text-slate-400">(add an account team lead first)</span>}
        </label>

        {emails.length > 0 && (
          <ul className="flex flex-col gap-1">
            {emails.map(e => (
              <li key={e.recordKey} className="flex items-start gap-2 text-xs text-slate-600">
                <span className="mt-0.5">{STATUS_ICON[e.status]}</span>
                <span className="flex-1 min-w-0">
                  <span className="font-semibold text-navy">{EMAIL_TYPE_LABELS[e.emailType]}</span>
                  {e.status === 'sent' && <> sent {when(e.sentAt)} to {e.to.join(', ')}{e.cc.length ? ` (cc ${e.cc.length})` : ''}</>}
                  {e.status === 'skipped' && <> skipped: {e.reason}</>}
                  {e.status === 'failed' && <> failed {when(e.createdAt)} (attempt {e.attempts} of {maxAttempts})</>}
                  {e.status === 'sending' && <> sending…</>}
                  {e.blocked.length > 0 && (
                    <span className="block text-amber-700">Not sent to (not on the allow list): {e.blocked.join(', ')}</span>
                  )}
                </span>
                {e.status === 'failed' && canRetry && e.attempts < maxAttempts && (
                  <button onClick={() => retry(e)} disabled={retrying === e.recordKey}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-60">
                    <RefreshCw className={`w-3 h-3 ${retrying === e.recordKey ? 'animate-spin' : ''}`} /> Retry
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
}
